import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { SearchDialog } from "@/components/layout/SearchDialog";
import { TopBarSlotContext } from "@/components/layout/TopBarSlot";
import { useMe, useUpdateSettings } from "@/hooks/queries";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { browserTimeZone } from "@/lib/tasks";
import { cn } from "@/lib/utils";

function useApplySettings(settings) {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.textSize = settings?.text_size || "default";
    root.dataset.reducedMotion = settings?.reduced_motion ? "true" : "false";
  }, [settings?.text_size, settings?.reduced_motion]);
}

// First use: save the browser's IANA timezone so task dates and reminders are interpreted correctly.
function useDetectTimezone(settings) {
  const update = useUpdateSettings();
  const tried = useRef(false);
  useEffect(() => {
    if (!settings || settings.timezone || tried.current) return;
    tried.current = true;
    update.mutate({ timezone: browserTimeZone() });
  }, [settings, update]);
}

export default function AppShell() {
  const { data: me } = useMe();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cs-sidebar") === "collapsed");
  const [searchOpen, setSearchOpen] = useState(false);
  const [slot, setSlot] = useState(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  useApplySettings(me?.settings);
  useDetectTimezone(me?.settings);

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      localStorage.setItem("cs-sidebar", v ? "open" : "collapsed");
      return !v;
    });
  };

  return (
    <TopBarSlotContext.Provider value={slot}>
      <div className="flex h-dvh overflow-hidden bg-ink-950 text-zinc-100" data-testid="app-shell">
        {isDesktop && (
          <aside
            className={cn(
              "flex shrink-0 overflow-hidden border-r border-white/[0.07] bg-ink-900 transition-[width] duration-200 ease-out",
              collapsed ? "w-0 border-r-0" : "w-[270px]",
            )}
            data-testid="app-sidebar"
          >
            <Sidebar me={me} />
          </aside>
        )}
        <Sheet open={!isDesktop && drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="w-[290px] max-w-[85vw] border-r border-white/[0.07] bg-ink-900 p-0 [&>button]:hidden" data-testid="mobile-sidebar-drawer">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Sidebar me={me} />
          </SheetContent>
        </Sheet>
        <main className="flex min-w-0 flex-1 flex-col">
          <TopBar
            me={me}
            onOpenDrawer={() => setDrawerOpen(true)}
            onToggleSidebar={toggleCollapsed}
            sidebarCollapsed={collapsed}
            onSearch={() => setSearchOpen(true)}
            slotRef={setSlot}
          />
          <div className="min-h-0 flex-1">
            <Outlet />
          </div>
        </main>
        <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      </div>
    </TopBarSlotContext.Provider>
  );
}
